/**
 * DocumentPreview - Document preview component using Owl Ops API.
 *
 * Renders templates via API calls. Uses debouncing to avoid excessive requests.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useTemplateDesigner } from './TemplateDesignerContext';
import { SAMPLE_DATA, SAMPLE_BUSINESS } from './sampleData';
import { generateBannerHtml, getIconSvgFromUrl } from './utils';
import { documentsApi } from '@/lib/api';
import type { DocumentData, BusinessData, DocumentType, LineItemSection, InvoiceData, TemplateSettings } from './types';

// Props for passing real document data (optional - falls back to SAMPLE_DATA)
interface DocumentPreviewProps {
  // Real invoice data from API
  invoiceData?: {
    invoice_number: string;
    invoice_date: string | null;
    due_date: string | null;
    payment_terms: string;
    status: string;
    line_items: [string, { name: string; code: string; description: string; quantity: number; rate: number; amount: number; tax_ids?: string[] }[]][];
    note_for_client: string;
    subtotal: string;
    total_markup: string;
    total_discount: string;
    total_amount: string;
    amount_paid: string;
    balance_due: string;
    client_name: string;
    client_company: string;
    client_address: string;
  };
  // Business data for "from" address
  businessData?: {
    name: string;
    address: string;
    phone?: string;
    email?: string;
    logo?: string;
  };
  // Override document type (useful when passing real data)
  documentType?: DocumentType;
}

/**
 * Transform API invoice data to DocumentData format
 */
function transformInvoiceData(
  invoiceData: NonNullable<DocumentPreviewProps['invoiceData']>,
  _documentType: DocumentType = 'invoice'
): DocumentData {
  const lineItems: LineItemSection[] = invoiceData.line_items.map(([section, items]) => [
    section,
    items.map(item => ({
      name: item.name,
      code: item.code || '',
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      item_type: 'product_parts' as const,
      discount: 0,
      discount_type: 'percentage' as const,
      tax_rate: 0,
      tax_amount: 0,
      amount: item.amount,
    })),
  ]);

  const baseData: InvoiceData = {
    documentType: 'invoice',
    id: 'real_invoice',
    invoice_number: invoiceData.invoice_number,
    client_name: invoiceData.client_name,
    client_company: invoiceData.client_company,
    client_address: invoiceData.client_address,
    invoice_date: invoiceData.invoice_date || '',
    due_date: invoiceData.due_date || '',
    payment_terms: invoiceData.payment_terms,
    status: invoiceData.status,
    line_items: lineItems,
    note_for_client: invoiceData.note_for_client,
    subtotal: parseFloat(invoiceData.subtotal),
    total_tax: 0,
    total_markup: parseFloat(invoiceData.total_markup),
    total_discount: parseFloat(invoiceData.total_discount),
    total_amount: parseFloat(invoiceData.total_amount),
    amount_paid: parseFloat(invoiceData.amount_paid),
    balance_due: parseFloat(invoiceData.balance_due),
  };

  return baseData;
}

/**
 * Transform business data to BusinessData format
 */
function transformBusinessData(
  businessData: NonNullable<DocumentPreviewProps['businessData']>
): BusinessData {
  return {
    name: businessData.name,
    address: businessData.address,
    phone: businessData.phone || '',
    email: businessData.email || '',
    logo: businessData.logo,
  };
}

/**
 * Build Owl Ops API data from settings and document data
 */
export function buildOwlOpsData(
  documentData: DocumentData,
  business: BusinessData,
  settings: TemplateSettings,
  documentType: DocumentType
): { data: Record<string, unknown>; settings: Record<string, unknown> } {
  const docType = documentData.documentType;

  const getNumber = (): string => {
    switch (docType) {
      case 'invoice': return (documentData as { invoice_number?: string }).invoice_number || '';
      case 'quote': return (documentData as { quote_number?: string }).quote_number || '';
      case 'credit_memo': return (documentData as { credit_memo_number?: string }).credit_memo_number || '';
      case 'purchase_order': return (documentData as { purchase_order_number?: string }).purchase_order_number || '';
      default: return '';
    }
  };

  const getDate = (): string => {
    switch (docType) {
      case 'invoice': return (documentData as { invoice_date?: string }).invoice_date || '';
      case 'quote': return (documentData as { quote_date?: string }).quote_date || '';
      case 'credit_memo': return (documentData as { credit_memo_date?: string }).credit_memo_date || '';
      case 'purchase_order': return (documentData as { issued_date?: string }).issued_date || '';
      default: return '';
    }
  };

  const getNotes = (): string => {
    switch (docType) {
      case 'invoice':
      case 'quote':
      case 'credit_memo':
        return (documentData as { note_for_client?: string }).note_for_client || '';
      case 'purchase_order':
        return (documentData as { note_for_vendor?: string }).note_for_vendor || '';
      default: return '';
    }
  };

  // Build columns from settings
  const columnTypeMap: Record<string, 'text' | 'number' | 'currency' | 'date' | 'percent'> = {
    name: 'text',
    code: 'text',
    description: 'text',
    quantity: 'number',
    rate: 'currency',
    tax: 'percent',
    amount: 'currency',
  };
  const columns = settings.tableColumns
    .filter(col => col.enabled)
    .map(col => ({
      key: col.id,
      label: col.name,
      type: columnTypeMap[col.id] || 'text',
      ...(col.width ? { width: col.width } : {}),
    }));

  // Build header fields (only custom fields for current doc type)
  const header_fields = settings.headerFields
    .filter(field => field.enabled && field.isCustom && field.documentTypes.includes(docType))
    .map(field => ({
      key: field.id,
      label: field.name,
      enabled: true,
      is_custom: true,
      value: field.defaultValue ?? '',
    }));

  // Helper to check if a built-in field is enabled
  const isFieldEnabled = (fieldId: string): boolean => {
    const field = settings.headerFields.find(f => f.id === fieldId && !f.isCustom);
    return field ? field.enabled : true; // Default to enabled if not found
  };

  // Flatten line items
  const line_items: Array<Record<string, unknown>> = [];
  const line_item_sections: Array<{ name: string; items: Array<Record<string, unknown>> }> = [];

  if (documentData.line_items) {
    for (const [sectionName, items] of documentData.line_items) {
      const sectionItems = items.map(item => ({
        name: item.name,
        code: item.code,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        tax: item.tax_rate,
        amount: item.amount,
      }));
      line_items.push(...sectionItems);
      line_item_sections.push({ name: sectionName, items: sectionItems });
    }
  }

  // Build totals
  const totals: Array<{
    key: string;
    label: string;
    value: number;
    style?: string;
    divider?: string;
  }> = [];
  if ('subtotal' in documentData) {
    totals.push({ key: 'subtotal', label: 'Subtotal', value: documentData.subtotal as number, style: 'default' });
  }
  // Discount comes before tax
  if ('total_discount' in documentData && (documentData as { total_discount?: number }).total_discount) {
    totals.push({ key: 'discount', label: 'Discount', value: -(documentData as { total_discount: number }).total_discount, style: 'addition' });
  }
  if ('total_tax' in documentData && (documentData as { total_tax?: number }).total_tax) {
    totals.push({ key: 'tax', label: 'Tax', value: (documentData as { total_tax: number }).total_tax, style: 'default' });
  }
  // Shipping comes after tax (shipping is not taxable)
  if ((documentData as { ship_enabled?: boolean }).ship_enabled) {
    const shipFee = (documentData as { ship_fee?: string | number }).ship_fee;
    if (shipFee) {
      const feeValue = typeof shipFee === 'string' ? parseFloat(shipFee) : shipFee;
      if (feeValue > 0) {
        totals.push({ key: 'shipping', label: 'Shipping', value: feeValue, style: 'default' });
      }
    }
  }
  // NOTE: Markup is intentionally NOT shown - it's internal pricing info, not for customer-facing documents
  // Only emphasize balance_due, not total (template uses first emphasized for the big box)
  const hasBalanceDue = 'balance_due' in documentData && (documentData as { balance_due?: number }).balance_due;
  if ('total_amount' in documentData) {
    totals.push({ key: 'total', label: 'Total', value: (documentData as { total_amount: number }).total_amount, style: 'default', divider: 'above' });
  }
  if ('amount_paid' in documentData && (documentData as { amount_paid?: number }).amount_paid) {
    totals.push({ key: 'paid', label: 'Amount Paid', value: -(documentData as { amount_paid: number }).amount_paid, style: 'addition' });
  }
  if (hasBalanceDue) {
    totals.push({ key: 'balance', label: 'Balance Due', value: (documentData as { balance_due: number }).balance_due, style: 'emphasized', divider: 'above' });
  }

  // Build client/vendor
  let client: Record<string, unknown> | undefined;
  let vendor: Record<string, unknown> | undefined;

  if (docType === 'purchase_order') {
    vendor = {
      id: 'vendor',
      name: (documentData as { vendor_name?: string }).vendor_name || '',
      company: (documentData as { vendor_company?: string }).vendor_company || '',
      address: (documentData as { vendor_address?: string }).vendor_address || '',
    };
  } else {
    client = {
      id: 'client',
      name: (documentData as { client_name?: string }).client_name || '',
      company: (documentData as { client_company?: string }).client_company || '',
      address: (documentData as { client_address?: string }).client_address || '',
    };
  }

  // Build business object
  // Logo goes here if it's a real image (data URL or http URL)
  // Icon placeholders are handled separately via settings.logo.iconSvg
  const businessObj: Record<string, unknown> = {
    id: business.id,
    name: business.name,
    address: business.address,
    phone: business.phone,
    email: business.email,
  };

  // Logo priority: settings.logo.url (template designer) > businessData.logo
  // Template designer upload always takes precedence
  // Note: icon: URLs are rendered via iconSvg, so don't fall back to business logo when icon is selected
  if (settings.logo.url && !settings.logo.url.startsWith('icon:')) {
    businessObj.logo = settings.logo.url;
  } else if (!settings.logo.url && business.logo) {
    businessObj.logo = business.logo;
  }

  // Build document object - conditionally include fields based on visibility settings
  const documentObj: Record<string, unknown> = {
    type: docType,
    id: documentData.id || 'preview',
    number: getNumber(),
    notes: getNotes(),
    columns,
    header_fields,
    line_items,
    line_item_sections,
    totals,
  };

  // Only include status if 'paid' - other statuses are not customer-facing
  const rawStatus = (documentData as { status?: string }).status;
  if (rawStatus === 'paid') {
    documentObj.status = 'paid';
  }

  // Only include date if enabled
  if (isFieldEnabled('date')) {
    documentObj.date = getDate();
  }

  // Only include due_date if enabled
  if (isFieldEnabled('due_date')) {
    documentObj.due_date = (documentData as { due_date?: string }).due_date;
  }

  // Only include payment_terms if enabled
  if (isFieldEnabled('terms')) {
    const rawTerms = (documentData as { payment_terms?: string }).payment_terms;
    // Format snake_case terms to nice display format (e.g., "net_30" -> "Net 30")
    const formatTerms = (terms: string | undefined): string | undefined => {
      if (!terms) return terms;
      // Already formatted nicely (contains space or starts with capital)
      if (terms.includes(' ') || /^[A-Z]/.test(terms)) return terms;
      // Convert snake_case: "net_30" -> "Net 30", "due_on_receipt" -> "Due On Receipt"
      return terms.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };
    documentObj.payment_terms = formatTerms(rawTerms);
  }

  // Build data object for API
  const data: Record<string, unknown> = {
    document: documentObj,
    business: businessObj,
  };

  // Include shipping info if enabled on document AND ship_to field is enabled in template
  // Ship To is the "master switch" - metadata fields only show when Ship To is enabled
  if ((documentData as { ship_enabled?: boolean }).ship_enabled && isFieldEnabled('ship_to')) {
    const shipDate = (documentData as { ship_date?: string }).ship_date;
    const shipVia = (documentData as { ship_via?: string }).ship_via;
    const shipTracking = (documentData as { ship_tracking_number?: string }).ship_tracking_number;

    // Add shipping metadata to header_fields (only if individual field is enabled and has a value)
    if (shipDate && isFieldEnabled('ship_date')) {
      header_fields.push({ key: 'ship_date', label: 'Ship Date', enabled: true, is_custom: false, value: shipDate });
    }
    if (shipVia && isFieldEnabled('ship_via')) {
      header_fields.push({ key: 'ship_via', label: 'Ship Via', enabled: true, is_custom: false, value: shipVia });
    }
    if (shipTracking && isFieldEnabled('tracking_no')) {
      header_fields.push({ key: 'tracking_no', label: 'Tracking No', enabled: true, is_custom: false, value: shipTracking });
    }

    // Build shipping address block (Ship To)
    if (docType === 'purchase_order') {
      // For PO: Ship To is the business address (where vendor ships to)
      data.shipping = {
        name: business.name,
        company: '',
        address: business.address || '',
      };
    } else {
      // For Invoice/Quote: Ship To uses client's primary address
      data.shipping = {
        name: (documentData as { client_name?: string }).client_name || '',
        company: (documentData as { client_company?: string }).client_company || '',
        address: (documentData as { client_address?: string }).client_address || '',
      };
    }
  }

  if (client) data.client = client;
  if (vendor) data.vendor = vendor;

  // Build settings object for API
  const bannerHtml = settings.banner.enabled
    ? generateBannerHtml(settings.banner.style, settings.colorTheme, settings.banner.height)
    : '';

  // Pass business name for initials icon generation
  const logoIconSvg = getIconSvgFromUrl(settings.logo.url, business.name);
  const watermarkIconSvg = settings.watermark.content?.startsWith('icon:')
    ? getIconSvgFromUrl(settings.watermark.content, business.name)
    : undefined;

  // Build footer
  const footer: Record<string, unknown> = {};
  if (settings.footer.genericNote?.enabled && settings.footer.genericNote?.content) {
    footer.genericNote = settings.footer.genericNote.content;
  }
  const docNote = settings.footer.documentNotes?.[documentType];
  if (docNote?.enabled && docNote?.content) {
    footer.documentNotes = docNote.content;
  }
  if (settings.footer.termsAndConditions?.enabled && settings.footer.termsAndConditions?.content) {
    footer.termsAndConditions = settings.footer.termsAndConditions.content;
  }
  if (settings.footer.taxBreakdown !== undefined) {
    footer.taxBreakdown = settings.footer.taxBreakdown;
  }

  // Build logo settings
  // Note: The actual logo image goes in data.business.logo, not here
  // settings.logo only contains display options and optional iconSvg for placeholders
  const logo: Record<string, unknown> = {
    size: settings.logo.size,
    showCompanyName: settings.logo.showCompanyName,
  };
  if (logoIconSvg) {
    // Icon placeholder (e.g., icon:briefcase) - send the SVG
    logo.iconSvg = logoIconSvg;
  }

  // Build watermark settings
  let watermark: Record<string, unknown>;
  if (settings.watermark.enabled) {
    watermark = {
      enabled: true,
      type: settings.watermark.type,
      opacity: Number(settings.watermark.opacity),
      size: Number(settings.watermark.size),
      position: settings.watermark.position,
      rotation: Number(settings.watermark.rotation),
    };
    // For icon watermarks, only iconSvg matters (not content)
    // For text watermarks, content is the text to display
    if (watermarkIconSvg) {
      watermark.iconSvg = watermarkIconSvg;
    } else if (settings.watermark.type === 'text' && settings.watermark.content) {
      watermark.content = settings.watermark.content;
    }
  } else {
    watermark = { enabled: false };
  }

  const apiSettings: Record<string, unknown> = {
    colorTheme: settings.colorTheme,
    banner: {
      enabled: settings.banner.enabled,
      height: `${settings.banner.height}px`,
      html: bannerHtml,
    },
    logo,
    watermark,
    footer,
    labels: settings.labels,
    dateFormat: settings.dateFormat,
  };

  return { data, settings: apiSettings };
}

/**
 * Generate CSS override string for color theme variables
 */
export function generateColorOverrideCss(colorTheme: TemplateSettings['colorTheme']): string {
  return `
    :root {
      --theme-primary: ${colorTheme.primary} !important;
      --theme-secondary: ${colorTheme.secondary} !important;
      --theme-accent: ${colorTheme.accent} !important;
      --theme-header-bg: ${colorTheme.headerBg} !important;
      --theme-header-text: ${colorTheme.headerText} !important;
      --theme-table-border: ${colorTheme.tableBorder} !important;
      --theme-table-header-bg: ${colorTheme.tableHeaderBg} !important;
      --theme-table-header-text: ${colorTheme.tableHeaderText} !important;
      --theme-table-alt-row: ${colorTheme.tableAltRow} !important;
      --theme-divider: ${colorTheme.divider} !important;
      --theme-total: ${colorTheme.total} !important;
    }
  `;
}

/**
 * Inject CSS override into HTML string (before closing </head> tag)
 */
export function injectColorOverride(html: string, colorTheme: TemplateSettings['colorTheme']): string {
  const overrideCss = generateColorOverrideCss(colorTheme);
  const styleTag = `<style id="color-override">${overrideCss}</style>`;

  // Insert before </head> if exists, otherwise before </body>
  if (html.includes('</head>')) {
    return html.replace('</head>', `${styleTag}</head>`);
  } else if (html.includes('</body>')) {
    return html.replace('</body>', `${styleTag}</body>`);
  }
  // Fallback: prepend
  return styleTag + html;
}

/**
 * Update CSS variables directly in iframe document (no reload)
 * Uses 'important' priority to override :root stylesheet rules
 */
export function updateIframeColors(doc: Document, colorTheme: TemplateSettings['colorTheme']): void {
  const root = doc.documentElement;
  root.style.setProperty('--theme-primary', colorTheme.primary, 'important');
  root.style.setProperty('--theme-secondary', colorTheme.secondary, 'important');
  root.style.setProperty('--theme-accent', colorTheme.accent, 'important');
  root.style.setProperty('--theme-header-bg', colorTheme.headerBg, 'important');
  root.style.setProperty('--theme-header-text', colorTheme.headerText, 'important');
  root.style.setProperty('--theme-table-border', colorTheme.tableBorder, 'important');
  root.style.setProperty('--theme-table-header-bg', colorTheme.tableHeaderBg, 'important');
  root.style.setProperty('--theme-table-header-text', colorTheme.tableHeaderText, 'important');
  root.style.setProperty('--theme-table-alt-row', colorTheme.tableAltRow, 'important');
  root.style.setProperty('--theme-divider', colorTheme.divider, 'important');
  root.style.setProperty('--theme-total', colorTheme.total, 'important');
}

/**
 * Create a stable key for non-color settings to detect structural changes
 * When banner is enabled, include colorTheme because banner HTML has baked-in colors
 */
function getNonColorSettingsKey(
  settings: TemplateSettings,
  activeDocType: DocumentType,
  data: DocumentData,
  business: BusinessData
): string {
  // If banner is enabled, include colorTheme in key (banner has hardcoded colors)
  // Otherwise exclude it - color changes can be applied via CSS variables
  if (settings.banner.enabled) {
    return JSON.stringify({ settings, activeDocType, data, business });
  }
  const { colorTheme: _, ...nonColorSettings } = settings;
  return JSON.stringify({ nonColorSettings, activeDocType, data, business });
}

export function DocumentPreview({ invoiceData, businessData, documentType }: DocumentPreviewProps = {}) {
  const {
    settings,
    previewDocumentType,
    isPreviewAnimating,
    templatesLoaded,
    availableTemplates,
  } = useTemplateDesigner();

  // Base HTML from API (without color overrides)
  const [baseHtml, setBaseHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastNonColorKeyRef = useRef<string>('');

  const activeDocType: DocumentType = documentType || previewDocumentType;

  // Build document data
  const data: DocumentData = useMemo(() =>
    invoiceData
      ? transformInvoiceData(invoiceData, activeDocType)
      : SAMPLE_DATA[activeDocType],
    [invoiceData, activeDocType]
  );

  // Build business data
  const business: BusinessData = useMemo(() =>
    businessData
      ? transformBusinessData(businessData)
      : SAMPLE_BUSINESS,
    [businessData]
  );

  // Check if ready to render
  const isReady = templatesLoaded && availableTemplates.includes(settings.selectedLayout);

  // Compute non-color settings key to detect structural changes
  // This key changes ONLY when non-color settings change
  const nonColorKey = useMemo(
    () => getNonColorSettingsKey(settings, activeDocType, data, business),
    [settings, activeDocType, data, business]
  );

  // Use ref to access latest values in callback without causing recreation
  const latestRef = useRef({ settings, activeDocType, data, business, nonColorKey });
  latestRef.current = { settings, activeDocType, data, business, nonColorKey };

  // Render template via API - stable callback that reads from ref
  const renderPreview = useCallback(async () => {
    if (!isReady) return;

    const { settings: s, activeDocType: docType, data: d, business: b, nonColorKey: key } = latestRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const { data: apiData, settings: apiSettings } = buildOwlOpsData(d, b, s, docType);

      const response = await documentsApi.renderHtml({
        template_slug: s.selectedLayout,
        data: apiData,
        settings: apiSettings,
      });

      if (response.data?.html) {
        setBaseHtml(response.data.html);
        lastNonColorKeyRef.current = key;
        setError(null);
      } else {
        throw new Error('No HTML returned from API');
      }
    } catch (err) {
      console.error('Failed to render template:', err);
      setError(err instanceof Error ? err.message : 'Failed to render template');
    } finally {
      setIsLoading(false);
    }
  }, [isReady]); // Only depends on isReady - reads latest values from ref

  // Debounced render ONLY when non-color settings change
  useEffect(() => {
    if (!isReady) return;

    // Skip API call if only colors changed (nonColorKey hasn't changed)
    if (lastNonColorKeyRef.current === nonColorKey && baseHtml) {
      return;
    }

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set loading state immediately for visual feedback
    setIsLoading(true);

    // Debounce the actual API call
    debounceRef.current = setTimeout(() => {
      renderPreview();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [renderPreview, isReady, nonColorKey, baseHtml]);

  // Ref to iframe for direct style manipulation
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Track the last baseHtml to detect structural changes vs color-only changes
  const lastBaseHtmlRef = useRef<string>('');

  // Compute HTML - only update srcDoc when baseHtml changes, not for color changes
  // Colors are applied via direct DOM manipulation after load
  const html = useMemo(() => {
    if (!baseHtml) return '';
    // Use current colorTheme for initial render, but don't re-run on colorTheme changes
    return injectColorOverride(baseHtml, settings.colorTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseHtml]); // Intentionally excluding colorTheme - colors updated via DOM

  // Try to update colors directly in iframe without reload
  // Only works after iframe has loaded and if we can access contentDocument
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !baseHtml) return;

    // If baseHtml changed, we need a full reload (srcDoc will handle it)
    if (lastBaseHtmlRef.current !== baseHtml) {
      lastBaseHtmlRef.current = baseHtml;
      return;
    }

    // Try to update colors directly (avoids iframe reload)
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.documentElement) {
        updateIframeColors(doc, settings.colorTheme);
      }
    } catch {
      // Cross-origin or other error - srcDoc update will handle it
    }
  }, [baseHtml, settings.colorTheme]);

  // Handle iframe load - apply current colors
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    try {
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (doc?.documentElement) {
        updateIframeColors(doc, settings.colorTheme);
      }
    } catch {
      // Ignore errors
    }
  }, [settings.colorTheme]);

  // Waiting for templates to load
  if (!templatesLoaded) {
    return (
      <div className="w-full aspect-[8.5/11] bg-white rounded-lg shadow-lg border border-border flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading templates...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !html) {
    return (
      <div className="w-full aspect-[8.5/11] bg-white rounded-lg shadow-lg border border-red-200 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-red-500 font-medium mb-2">Failed to render preview</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            onClick={renderPreview}
            className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading && !html) {
    return (
      <div className="w-full aspect-[8.5/11] bg-white rounded-lg shadow-lg border border-border flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Rendering preview...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        relative w-full aspect-[8.5/11] bg-white rounded-lg shadow-lg border border-border overflow-hidden
        transition-opacity duration-200
        ${isLoading || isPreviewAnimating ? 'opacity-70' : 'opacity-100'}
      `}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      <iframe
        ref={iframeRef}
        srcDoc={html}
        onLoad={handleIframeLoad}
        className="w-full h-full border-0"
        title="Document Preview"
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
}
